import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { Information } from "@/components/Information/Information";

import {
	SalesforceListSyncTriggerResponse,
	SalesforceSyncTriggerWithInherit,
} from "../../../../Company/schema/salesforce";

interface SalesforceSyncSectionProps {
	isSalesforceEnabled: boolean;
	isConnected: boolean;
	listSyncTrigger: SalesforceListSyncTriggerResponse | undefined;
	selectedSalesforceSyncTrigger: SalesforceSyncTriggerWithInherit | undefined;
	setSelectedSalesforceSyncTrigger: (trigger: SalesforceSyncTriggerWithInherit) => void;
}

/**
 * The list-level override of the Salesforce **record sync** trigger — and nothing else.
 *
 * Unlike the HubSpot section there is no per-list opportunity-creation trigger to render:
 * `salesforce_deal_creation_trigger` has a unique `companyId` and no list column, so that
 * setting exists once, in the company settings tab.
 */
export const SalesforceSyncSection: FunctionComponent<SalesforceSyncSectionProps> = ({
	isSalesforceEnabled,
	isConnected,
	listSyncTrigger,
	selectedSalesforceSyncTrigger,
	setSelectedSalesforceSyncTrigger,
}) => {
	const { t } = useTranslation();
	const tsSettings = (key: string) => t(`list.settings.${key}`);

	if (!isSalesforceEnabled || !isConnected) {
		return null;
	}

	return (
		<div className="mt-8 flex w-full flex-col" data-testid="salesforce-list-sync-section">
			<div className="mb-4 flex w-full flex-col gap-2">
				<div className="text-ssm font-medium text-text">
					{tsSettings("salesforce.syncTrigger.title")}{" "}
					<Information information={tsSettings("salesforce.syncTrigger.description")} />
				</div>
			</div>
			<Dropdown
				data={[
					{
						title: tsSettings("salesforce.syncTrigger.INHERIT"),
						id: SalesforceSyncTriggerWithInherit.INHERIT,
					},
					{
						title: tsSettings("salesforce.syncTrigger.DISABLED"),
						id: SalesforceSyncTriggerWithInherit.DISABLED,
					},
					{
						title: tsSettings("salesforce.syncTrigger.ON_CAMPAIGN_START"),
						id: SalesforceSyncTriggerWithInherit.ON_CAMPAIGN_START,
					},
					{
						title: tsSettings("salesforce.syncTrigger.ON_REPLY"),
						id: SalesforceSyncTriggerWithInherit.ON_REPLY,
					},
				]}
				defaultValue={
					selectedSalesforceSyncTrigger
						? {
								id: selectedSalesforceSyncTrigger,
								title: tsSettings(`salesforce.syncTrigger.${selectedSalesforceSyncTrigger}`),
							}
						: undefined
				}
				floating={true}
				handleSelect={(value: DropdownAutoCompleteItem) => {
					setSelectedSalesforceSyncTrigger(value.id as SalesforceSyncTriggerWithInherit);
				}}
			/>
			{listSyncTrigger?.isInherited && (
				<div className="mt-2 text-ssm text-text-meta">
					{tsSettings("salesforce.syncTrigger.inheritedFrom")}:{" "}
					{tsSettings(`salesforce.syncTrigger.${listSyncTrigger.effectiveTrigger}`)}
				</div>
			)}
		</div>
	);
};
